import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import { getCardCtaLinkProps } from '@/app/components/search/components/EvolutionCard/utils/getCardCtaLinkProps.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import Stars from '@/app/common/components/Stars';
import Spacer from '@/app/components/search/components/EvolutionCard/Spacer';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { getCardHref } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import { useSustainabilityCertificateLabel } from '@/app/common/hooks/useSustainabilityCertificateLabel.ts';
import Certification from '@/app/common/components/Certification/index.tsx';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { CARD_TYPE, type HandleDealCardCtaCallBack } from '../types';
import { DetailsContainer, DetailsInnerContainer, HotelName, HotelNameLink } from './styled';
import Ratings from './Ratings';
import Features from './Features';
import Price from './Price';
import LocationText from './LocationText';
import { hasPromotion } from '../utils/getPromotionsUtils';
import { ClickEventNameEnum } from '../../../Curiosity/Analytics/utils/ClickEventNameEnum';
import CardDetailsLoader from '../Loaders/Details';
import PriceLoader from '../Loaders/Price';
import { PriceContainer } from './Price/styled';
import BadgeSlot from '../Badges/BadgeSlot.tsx';
import { BADGE_SLOTS } from '../Badges/types';

const AlternativeDates = dynamic(() => import('./AlternativeDates'), { ssr: false });

type DetailsProps = {
  dealCard?: DealCard;
  isOverlay: boolean;
  isNotClickable: boolean;
  promotionType: PROMOTION_TYPES | null;
  handleDealCardCtaCallBack: HandleDealCardCtaCallBack;
  promotionTypes: PROMOTION_TYPES[];
  type?: CARD_TYPE;
};

const Details = ({
  dealCard,
  isOverlay,
  isNotClickable,
  promotionType,
  handleDealCardCtaCallBack,
  promotionTypes,
  type,
}: DetailsProps) => {
  const { isMobile } = useCommonClient();
  const { searchInfoData, commonData } = usePageWrapperData();
  const { isLoading, isWaiting } = useContext(CardsContext);
  const { listView } = useContext(SearchPageContext);
  const certificationLabel = useSustainabilityCertificateLabel(dealCard?.product?.accommodation);

  if (!dealCard?.product?.accommodation || (isLoading && !isWaiting)) {
    return (
      <DetailsContainer isOverlay={isOverlay} data-testid="details" isNotClickable={!isOverlay}>
        <DetailsInnerContainer isOverlay={isOverlay}>
          <CardDetailsLoader isOverlay={isOverlay} />
          <Features dealCard={undefined} promotionTypes={promotionTypes} isOverlay={isOverlay} />
        </DetailsInnerContainer>
        <PriceContainer isOverlay={isOverlay} isWaiting={isWaiting}>
          <PriceLoader isOverlay={isOverlay} isMobile={isMobile} />
        </PriceContainer>
      </DetailsContainer>
    );
  }

  const { product } = dealCard;
  const { accommodation, dates } = product;
  const soldOut = dates?.soldOut;
  const hotelName = accommodation?.name;
  const soldOutNotLoading = soldOut && !isLoading && !isWaiting;
  const isTsh = hasPromotion(PROMOTION_TYPES.TOP_SECRET_HOTELS, promotionTypes);
  const showAlternativeDates = soldOut && !isOverlay;
  const deepLink = getCardHref({ dealCard, searchInfoData, commonData });
  const isHO = searchInfoData?.searchMode === SearchModes.HO;
  const showFeatures = type !== CARD_TYPE.MODAL;
  const stars = accommodation?.stars;
  const starsEl = stars ? (
    <Stars stars={stars} black={true} style={{ marginLeft: 0, display: 'inline-flex' }} className="stars" />
  ) : null;

  return (
    <DetailsContainer
      {...(isNotClickable || !isOverlay
        ? {}
        : {
            as: 'a',
            href: deepLink,
            target: '_blank',
            onClick: (e: React.MouseEvent<HTMLDivElement>) =>
              handleDealCardCtaCallBack({ e, payload: { event_name: ClickEventNameEnum.hotelCardClick } }),
          })}
      isOverlay={isOverlay}
      data-testid="details"
      isNotClickable={!isOverlay}
    >
      <DetailsInnerContainer soldOut={soldOutNotLoading} isOverlay={isOverlay}>
        <HotelName
          variant={isMobile && isOverlay ? 'Body' : isMobile || isOverlay ? 'Title4' : 'Title3'}
          // Rating renders <div>s; 'Body' maps to <p>, which cannot contain them
          forceTag={isMobile && isOverlay ? 'div' : undefined}
          $isOverlay={isOverlay}
          $isTSH={isTsh}
          id={`card-title-${accommodation.hotelId}`}
        >
          {!isOverlay && !isNotClickable ? (
            <HotelNameLink {...getCardCtaLinkProps({ deepLink, handleDealCardCtaCallBack })}>
              <span>{hotelName}</span>
              {starsEl}
            </HotelNameLink>
          ) : (
            <>
              <span>{hotelName}</span>
              {starsEl}
            </>
          )}
          <BadgeSlot
            name={BADGE_SLOTS.HOTEL_HEADER}
            dealCard={dealCard}
            promotionTypes={promotionTypes}
            isOverlay={isOverlay}
          />
        </HotelName>
        <LocationText dealCard={dealCard} isOverlay={isOverlay} />
        <Ratings dealCard={dealCard} isOverlay={isOverlay} hasCertificationBelow={!!certificationLabel} />
        <Certification label={certificationLabel} isOverlay={isOverlay} variant="evoCard" />
        {!isOverlay && !soldOutNotLoading && <Spacer hideOnMobile={true} minHeight={listView ? '24px' : undefined} />}
        {showFeatures && <Features dealCard={dealCard} promotionTypes={promotionTypes} isOverlay={isOverlay} />}
        {showAlternativeDates && (
          <AlternativeDates dealCard={dealCard} handleDealCardCtaCallBack={handleDealCardCtaCallBack} />
        )}
      </DetailsInnerContainer>
      {!soldOutNotLoading && (
        <Price
          {...{
            dealCard,
            isOverlay,
            isNotClickable,
            promotionType,
            handleDealCardCtaCallBack,
            promotionTypes,
            deepLink,
            isHO,
          }}
        />
      )}
    </DetailsContainer>
  );
};

export default Details;
